/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useRef } from 'react';
import { Typography, Box, Divider, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Paper, useMediaQuery } from '@mui/material';
import { useTranslation } from 'react-i18next';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { useTheme } from '@emotion/react';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import Popup from '../../components/popups/popup';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import OnOffSwitchForDeleted from '../../components/inputs/onOffSwitchForDeleted';
import NotificationService from '../../services/notificationService';
import { returnDescriptionByLang, returnTitleByLang } from '../../utils';
import SenderDropdown from '../../components/dropdowns/senderDropdown';
import ReciverDropdown from '../../components/dropdowns/reciverDropdown';
import DeleteNotificationModal from '../../components/modals/notification/deleteNotificationModal';
import ViewNotificationModal from '../../components/modals/notification/viewNotificationModal';
import TimeDropdown from '../../components/dropdowns/timeDropdown';

const Notifications = () => {
    const { t, i18n } = useTranslation();
    const theme = useTheme();
    const isEn = i18n.language === 'en';
    const tableHeadingStyle = { fontWeight: 'bold', backgroundColor: theme.palette.customGrey.tableHeading, cursor: 'pointer' };
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));

    const [notifications, setNotifications] = useState([]);
    const [selectedNotification, setSelectedNotification] = useState({});

    const [selectedSender, setSelectedSender] = useState(null);
    const [selectedReciver, setSelectedReciver] = useState(null);
    const [selectedTime, setSelectedTime] = useState({
        time: 'today',
        value: t('today')
    });
    const [sortState, setSortState] = useState({ sortBy: '', order: '' });

    const [openDelete, setOpenDelete] = useState(false);
    const [openView, setOpenView] = useState(false);
    const [deleted, setDeleted] = useState(false);

    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const tableContainerRef = useRef(null);
    const isFirstRender = useRef(true);

    const [showPopup, setShowPopup] = useState(false);
    const [popupMsg, setPopupMsg] = useState(false);
    const [popupType, setPopupType] = useState(false);
    const [isLoading, setIsLoading] = useState(false);


    const onSort = (sort) => {
        let order = '';
        if (sort === sortState.sortBy || sortState.sortBy === '') {
            switch (sortState.order) {
                case '':
                    order = 'asc';
                    break;
                case 'asc':
                    order = 'desc';
                    break;
                case 'desc':
                    order = '';
                    break;
                default:
                    order = '';
                    break
            }
        }

        setSortState({ sortBy: order === '' ? '' : sort, order });
    };

    const sortIcon = () => {
        let icon = <></>;
        switch (sortState.order) {
            case 'asc':
                icon = <ArrowDownwardOutlinedIcon fontSize="small" />;
                break;
            case 'desc':
                icon = <ArrowUpwardOutlinedIcon fontSize="small" />;
                break;
            default:
                icon = <ArrowDownwardOutlinedIcon fontSize="small" sx={{ color: 'transparent' }} />;
                break
        }

        return icon;
    };

    const handleOpenView = (notification) => {
        setSelectedNotification(notification);
        setOpenView(true);
    };;
    const handleCloseView = () => setOpenView(false);


    const handleOpenDelete = (notification) => {
        setSelectedNotification(notification);
        setOpenDelete(true);
    };
    const handleCloseDelete = async ({ isReaload }) => {
        setOpenDelete(false);
        if (isReaload) {
            setHasMore(true);
            setPage(1);
            setSelectedSender(null);
            setSelectedReciver(null);
            await fetchNotifications();
        }
    };

    const handleString = (str) => {
        if (str === t("driver")) return "driver";
        else if (str === t("user")) return "user";
        else if (str === t("page")) return "page";
    };

    const fetchNotifications = async (page = 1, sender = '', reciver = '') => {
        if (isLoading) return;
        setIsLoading(true);
        try {
            const filters = {
                sender: handleString(sender),
                reciver: handleString(reciver),
                deleted,
                ...sortState,
                ...selectedTime
            };
            const data = await NotificationService.fetch(page, filters);

            if (data.length === 0) {
                setHasMore(false);
                if (page === 1) {
                    setNotifications([]);
                }
            } else {
                setNotifications((prevs) => (page === 1 ? data : [...prevs, ...data]));
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const resetFilter = () => {
        setSelectedSender(null);
        setSelectedReciver(null);
        setHasMore(true);
        setSelectedTime({
            time: 'today',
            value: t('today')
        });
        if (page !== 1) setPage(1);
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }

        if (page === 1) fetchNotifications(page, selectedSender, selectedReciver);
    }, [page]);

    useEffect(() => {
        if (page !== 1) {
            isFirstRender.current = true;
            setPage(1);
        }
        fetchNotifications(1, selectedSender, selectedReciver);
    }, [selectedSender, selectedReciver, sortState, deleted, selectedTime]);

    const handleScroll = (event) => {
        const container = event.currentTarget;

        if (container.scrollTop + container.clientHeight >= container.scrollHeight - 50) {
            if (hasMore && !isLoading) {
                fetchNotifications(page + 1, selectedSender, selectedReciver);
                setPage(prevPage => prevPage + 1);
            }
        }
    };


    return (
        <Box sx={{
            p: isTablet ? 0.6 : 2,
            backgroundColor: theme.palette.secondary.main,
            width: '100%',
        }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <DeleteNotificationModal open={openDelete} handleClose={handleCloseDelete} setPopupMsg={setPopupMsg}
                    setPopupType={setPopupType}
                    setShowPopup={setShowPopup} notification={selectedNotification} />
                <ViewNotificationModal open={openView} handleClose={handleCloseView} notificationId={selectedNotification.id} />
            </Box>
            {showPopup && (
                <Popup
                    text={popupMsg}
                    type={popupType}
                    onClose={() => setShowPopup(false)}
                />
            )}

            {/* Filter row */}
            {
                isTablet ? <Box
                    sx={{
                        mb: 2,
                        border: 1,
                        borderColor: theme.palette.customGrey.borderInput,
                        backgroundColor: 'white',
                        borderRadius: 1,

                    }}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 50,
                    }}>
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box sx={{
                            alignContent: 'center',
                            height: 50,
                            width: '90%'
                        }}>
                            <TimeDropdown
                                isFilter={true}
                                selectedTime={selectedTime}
                                setSelectedTime={setSelectedTime}
                            />
                        </Box>
                    </Box>

                    <Divider />

                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <SenderDropdown
                            isFilter={true}
                            selectedSender={selectedSender}
                            setSelectedSender={setSelectedSender}
                        />
                    </Box>

                    <Divider />

                    <Box sx={{
                        alignContent: 'center',
                        height: 50,
                        width: '100%'
                    }}>
                        <ReciverDropdown
                            isFilter={true}
                            selectedReciver={selectedReciver}
                            setSelectedReciver={setSelectedReciver}
                        />
                    </Box>

                    <Divider />
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        height: 50,
                    }}>


                        <Divider orientation="vertical" />

                        <Box sx={{
                            ml: isEn ? 3 : 0
                        }}>
                            <OnOffSwitchForDeleted
                                checkDeleted={deleted}
                                setCheckDeleted={setDeleted} />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>



                </Box>
                    :
                    <Box
                        sx={{
                            mb: 2,
                            display: 'flex',
                            alignItems: 'center',
                            border: 1,
                            borderColor: theme.palette.customGrey.borderInput,
                            backgroundColor: 'white',
                            borderRadius: 1,
                            height: 60,
                        }}
                    >
                        <Box sx={{ px: 2 }}>
                            <FilterAltOutlinedIcon />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'40%'}>
                            <TimeDropdown
                                isFilter={true}
                                selectedTime={selectedTime}
                                setSelectedTime={setSelectedTime}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'17%'}>
                            <SenderDropdown
                                isFilter={true}
                                selectedSender={selectedSender}
                                setSelectedSender={setSelectedSender}
                            />
                        </Box>
                        <Divider orientation="vertical" />
                        <Box width={'17%'}>
                            <ReciverDropdown
                                isFilter={true}
                                selectedReciver={selectedReciver}
                                setSelectedReciver={setSelectedReciver}
                            />
                        </Box>
                        <Divider orientation="vertical" sx={{ mr: isEn ? 3 : 0 }} />

                        <OnOffSwitchForDeleted
                            checkDeleted={deleted}
                            setCheckDeleted={setDeleted} />
                        <Divider orientation="vertical" sx={{ mr: isEn ? 0 : 3 }} />

                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                color: theme.palette.text.red,
                                px: 2,
                                cursor: 'pointer',
                            }}
                            onClick={resetFilter}
                        >
                            <ReplayOutlinedIcon />
                            <Box width={3}></Box>
                            <Typography variant="subtitle2">{t('resetFilter')}</Typography>
                        </Box>
                    </Box>
            }

            {/* Scrollable Table with Sticky Header */}
            <TableContainer
                component={Paper}
                ref={tableContainerRef}
                onScroll={handleScroll}
                sx={{
                    boxShadow: 'none',
                    border: 1,
                    borderColor: theme.palette.customGrey.borderInput,
                    borderRadius: 2,
                    height: 'calc(100vh - 160px)',
                    overflowY: 'auto',
                }}
            >
                <Table stickyHeader aria-label="simple table">
                    <TableHead
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <TableRow>
                            <TableCell align={isEn ? 'left' : 'right'}
                                onClick={() => onSort('id')} sx={tableHeadingStyle}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('id')}
                                    {sortState.sortBy === 'id' && sortIcon()}
                                </Box>
                            </TableCell>
                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('sender')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('sender')}
                                    {sortState.sortBy === 'sender' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('reciver')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('reciver')}
                                    {sortState.sortBy === 'reciver' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }} >
                                {t('title')}
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                {t('description')}
                            </TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={tableHeadingStyle}
                                onClick={() => onSort('createdAt')}>
                                <Box sx={{ display: 'flex' }} >
                                    {t('createdAt')}
                                    {sortState.sortBy === 'createdAt' && sortIcon()}
                                </Box></TableCell>

                            <TableCell align={isEn ? 'left' : 'right'} sx={{ ...tableHeadingStyle, cursor: 'auto' }}>
                                {t('actions')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {notifications.map((notification, index) => (
                            <TableRow
                                key={notification.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                <TableCell align={isEn ? 'left' : 'right'} component="th" scope="row">{notification.id}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{t(notification.sender)}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{t(notification.reciver)}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{returnTitleByLang(notification, i18n.language)}</TableCell>
                                <TableCell
                                    align={isEn ? 'left' : 'right'}
                                    sx={{
                                        maxWidth: isTablet ? '100px' : '250px',
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis'
                                    }}
                                    title={returnDescriptionByLang(notification, i18n.language)}
                                >
                                    {returnDescriptionByLang(notification, i18n.language)}
                                </TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>{notification.createdAt}</TableCell>
                                <TableCell align={isEn ? 'left' : 'right'}>
                                    <Box sx={{
                                        display: 'inline-flex',
                                        border: 1,
                                        borderColor: theme.palette.customGrey.borderInput,
                                        backgroundColor: theme.palette.secondary.main,
                                        borderRadius: 1,
                                    }}>
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.6,
                                            pb: 0.25,
                                            cursor: 'pointer'
                                        }} onClick={() => handleOpenView(notification)}>
                                            <RemoveRedEyeOutlinedIcon fontSize='small'
                                                sx={{ color: theme.palette.text.primary }} />
                                        </Box>
                                        <Divider orientation="vertical" flexItem />
                                        {notification.deletedAt === null && <Divider orientation="vertical" flexItem />}
                                        <Box sx={{
                                            px: 1.5,
                                            pt: 0.4,
                                            pb: 0.25,
                                            cursor: 'pointer',
                                            pointerEvents: 'auto',
                                            opacity: 1,
                                        }} onClick={() => handleOpenDelete(notification)}>
                                            {notification.deletedAt === null ?
                                                <DeleteOutlineOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.red.bg }} />
                                                :
                                                <ReplayOutlinedIcon fontSize='small'
                                                    sx={{ color: theme.palette.green.bg }} />
                                            }
                                        </Box>

                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default Notifications;
